import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Button } from 'transfix-ui/components/Button'
import { Tabs, Tab } from 'transfix-ui/components/DeprecatedTabs'
import { InlineMessage, INLINE_MESSAGE_VARIANTS } from 'transfix-ui/components/InlineMessage'
import { Pagination } from 'transfix-ui/components/Pagination'
import { Box, Typography } from 'transfix-ui/core'
import { useFeatureFlag } from 'utils/context/FeatureFlagsContext'
import { ISpotBiddingConfig, ISpotBiddingConfigsParams } from '../types/spotBiddingConfig'
import { getSpotBiddingConfigList, updatePageCount } from '../utils/actions/spotBiddingConfigList'
import { IStore } from '../utils/reducers/types'
import { AutobiddersPanel } from './Autobidders/AutobiddersPanel'
import { CostControlPanel } from './CostControlPanel'
import { DeleteSpotBiddingConfigModal } from './DeleteSpotBiddingConfigModal'
import { ShipperConfig } from './ShipperConfig'
import { SpotBiddingConfigFilterContainer as SpotBiddingConfigFilter } from './SpotBiddingConfigFilterContainer'
import { SpotBiddingConfigModal } from './SpotBiddingConfigModal'
import { SpotBiddingConfigsTable } from './SpotBiddingConfigsTable'
import { StrikePriceControlPanel } from './StrikePriceControlPanel'

interface ISpotBiddingConfigsProps {
  spotBiddingConfigs: ISpotBiddingConfig[]
  fetching: boolean
  listError?: string
  updateError?: string
  updateLoading?: boolean
  totalCount: number
  params: ISpotBiddingConfigsParams
}

enum TABS {
  shipperRules = 'Shipper Rules',
  globalRules = 'Global Rules',
  costControlPanel = 'Cost Control Panel',
  strikePriceControlPanel = 'Strike Price Control Panel',
  autobiddersAndScrapers = 'Autobidders & Scrapers',
}

export const SpotControlPanelHome = () => {
  const autobiddersPanelEnabled = useFeatureFlag('autobiddersConfigurationPage')

  const [showSpotBiddingConfigModal, setSpotBiddingConfigModal] = useState<boolean>(false)
  const addConfigHandler = () => setSpotBiddingConfigModal(true)
  const closeModalHandler = () => setSpotBiddingConfigModal(false)

  const [editingConfig, setEditingConfig] = useState<ISpotBiddingConfig | undefined>(undefined)
  const handleOpenEditModal = (config?: ISpotBiddingConfig) => {
    setEditingConfig(config)
    setSpotBiddingConfigModal(true)
  }

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const showDeleteModalHandler = () => setShowDeleteModal(true)
  const closeDeleteModalHandler = () => setShowDeleteModal(false)

  const [configToDelete, setConfigToDelete] = useState<ISpotBiddingConfig | undefined>(undefined)
  const handleOpenDeleteModal = (config?: ISpotBiddingConfig) => {
    setConfigToDelete(config)
    showDeleteModalHandler()
  }

  const [currentTab, changeTab] = useState(TABS.shipperRules)
  const changeTabHandler = (newTab: TABS) => changeTab(newTab)

  const dispatch = useDispatch()

  const {
    listError,
    params,
    spotBiddingConfigs,
    totalCount,
    updateError,
    updateLoading,
  } = useSelector<IStore, ISpotBiddingConfigsProps>(
    ({ spotBiddingConfig, spotBiddingConfigList }) => ({
      spotBiddingConfigs: spotBiddingConfigList.spotBiddingConfigs,
      fetching: spotBiddingConfigList.fetching,
      updateLoading: spotBiddingConfig.fetching,
      updateError: spotBiddingConfig.error,
      listError: spotBiddingConfigList.error,
      params: spotBiddingConfigList.params,
      totalCount: spotBiddingConfigList.totalCount,
    })
  )

  useEffect(() => {
    dispatch(
      getSpotBiddingConfigList({
        ...params,
      })
    )
  }, [dispatch, params])

  useEffect(() => {
    if (!showSpotBiddingConfigModal) {
      setEditingConfig(undefined)
      dispatch(
        getSpotBiddingConfigList({
          ...params,
        })
      )
    }
    if (!showDeleteModal) {
      setConfigToDelete(undefined)
      dispatch(
        getSpotBiddingConfigList({
          ...params,
        })
      )
    }
  }, [dispatch, showSpotBiddingConfigModal, showDeleteModal])

  useEffect(() => {
    if (!updateLoading && !updateError) {
      closeModalHandler()
      closeDeleteModalHandler()
    }
  }, [updateLoading, updateError])

  const onPageChange = (newPageNumber: number) => {
    dispatch(updatePageCount(newPageNumber - 1))
  }

  return (
    <>
      <SpotBiddingConfigModal
        showModal={showSpotBiddingConfigModal}
        closeModalHandler={closeModalHandler}
        configToEdit={editingConfig}
        error={updateError}
      />
      <DeleteSpotBiddingConfigModal
        closeModalHandler={closeDeleteModalHandler}
        showModal={showDeleteModal}
        configId={configToDelete?.id}
        error={updateError}
      />
      <div className='container-fluid pt8 pb1'>
        {listError && (
          <InlineMessage type={INLINE_MESSAGE_VARIANTS.ALERT} message={listError || ''} />
        )}
        <div className='row mb3 mh3'>
          <Typography variant='h2' data-test='title'>
            Spot Control Panel
          </Typography>
          <div className='fr'>
            <Button color='primary' onClick={addConfigHandler} data-test='add-config-button'>
              Create New
            </Button>
          </div>
        </div>
        <Tabs dataTest='spot-bidding-config-tabs' value={currentTab} onChange={changeTabHandler}>
          <Tab label={TABS.shipperRules}>
            <Box p={4}>
              <SpotBiddingConfigFilter />
              <SpotBiddingConfigsTable
                spotBiddingConfigs={spotBiddingConfigs}
                totalCount={totalCount}
                params={params}
                openEditModal={handleOpenEditModal}
                openDeleteModal={handleOpenDeleteModal}
              />
              <Box textAlign='center'>
                <Pagination
                  currentPage={(params.pageCount || 0) + 1}
                  numberOfPages={Math.ceil(totalCount / params.perPage)}
                  onPageChange={onPageChange}
                />
              </Box>
            </Box>
          </Tab>
          <Tab label={TABS.globalRules}>
            <ShipperConfig displayTitle={false} displayQuoteType={false} />
          </Tab>
          <Tab label={TABS.costControlPanel}>
            <CostControlPanel />
          </Tab>
          <Tab label={TABS.strikePriceControlPanel}>
            <StrikePriceControlPanel />
          </Tab>
          {autobiddersPanelEnabled && (
            <Tab label={TABS.autobiddersAndScrapers}>
              <AutobiddersPanel />
            </Tab>
          )}
        </Tabs>
      </div>
    </>
  )
}
